/**
 * What a thrown value is, for the issue tracker: its class, its severity, the
 * line shown as its message and the group it belongs to.
 *
 * The tracker's own grouping is by JavaScript stack trace. Every failed
 * mutation has the same stack, so an analyzer crash, a Hasura permission error
 * and an expired session used to be one "error" there, and every thrown
 * GraphQL error object another. The classes below follow the cause instead:
 *
 *   what happened                                   class            severity  one group per
 *   analyzer rejected the study (USER_FACING)       UserFacingError  info      reason code
 *   analyzer or Lambda failed                       AnalysisError    error     phantom, reason, detail
 *     (ANALYSIS_SYSTEM_ERROR)
 *   record not found                                NotFoundError    info      entity
 *   address no route matches (mistyped, pasted      NotFoundError    info      (one group)
 *     with the words after it, an old bookmark)
 *   app server error (INTERNAL_SERVER_ERROR)        ServerError      error     operation, message
 *   app server refused with its own code            ServerMessage    info      code, operation
 *     (BAD_USER_INPUT, ACCOUNT_DISABLED, ...)
 *   Hasura rejected the request                     HasuraError      error     code, operation, message
 *     (validation-failed, constraint-violation, ...)
 *   session expired (invalid-jwt)                   SessionExpired   info      (one group)
 *   no response (offline, server unreachable)       NetworkError     warning   (one group)
 *   HTTP failure                                    NetworkError     warning   HTTP status, operation
 *   message the client shows the user               UserMessage      info      message
 *     (MessageError; a named subclass keeps its     (its name)       warning   its name
 *     name)
 *   failure the UI handled quietly                  HandledFailure   warning   the named operation and
 *     (issueTracker.reportHandled)                                             the class of its cause
 *   a non-Error value was thrown                    ThrownValue      warning   its text
 *   anything else (TypeError, DevError, ...)        its own name     error     the tracker's stack grouping
 *
 * "message", "detail" and "text" are normalised for grouping (ids, numbers,
 * file paths and double-quoted values replaced), so the same failure on two
 * studies, or for two user names, is one group.
 *
 * Pure: no tracker, no window, no Angular. `context` is what errorContext.ts
 * holds for the value (the GraphQL operation it came from).
 */
import { AnalysisFailure, getAnalysisFailure } from './analysisFailure'
import { ErrorContext } from './errorContext'
import { MessageError, NotFoundError } from './errors'
import {
  codeOf,
  GraphQLErrorLike,
  graphQLErrorsOf,
  unwrap,
} from './graphqlErrors'
import { isNavError } from './helpers'
import { getUserFacingDetails } from './userFacing'

export type Severity = 'info' | 'warning' | 'error'

export interface Classification {
  errorClass: string
  severity: Severity
  /** the technical line shown as the event's message */
  message: string
  /** events with the same hash are one error in the tracker; undefined = the tracker's stack grouping */
  groupingHash?: string
  /** what the server or the network said, when the failure is a GraphQL or HTTP one */
  response?: {
    code?: string
    reason?: string
    path?: string
    status?: number
  }
  /** the analyzer failure behind an AnalysisError */
  analysis?: AnalysisFailure
  /** extra tabs for the event, by tab name */
  tabs?: { [tab: string]: { [key: string]: unknown } }
}

/**
 * A failure a handler dealt with on its own (fell back to a default, showed
 * its own message) and therefore never reached the global error handler.
 * issueTracker.reportHandled() wraps it in one of these so it is reported
 * under the name of what failed.
 */
export class HandledFailure extends Error {
  constructor(
    /** what failed, e.g. 'load the site timezone'; the tracker groups on it */
    readonly operation: string,
    readonly cause: unknown,
  ) {
    super(operation)
    this.name = 'HandledFailure'
  }
}

const MAX_GROUPING_LENGTH = 160
const MAX_TEXT_LENGTH = 500

/**
 * `text` with everything that differs between two occurrences of the same
 * failure replaced: double-quoted values (`Username "jsmith" is already
 * taken`), UUIDs, file paths, hex ids, numbers. At most 160 characters.
 * Single-quoted words stay: in an analyzer's message they are type and
 * attribute names (`'NoneType' object has no attribute 'air'`).
 */
export function normaliseForGrouping(text: string): string {
  return String(text)
    .replace(/"[^"]*"/g, '<value>')
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '<uuid>',
    )
    .replace(/(?:\/[\w.\-@%+~]+){2,}\/?/g, '<path>')
    .replace(/\b0x[0-9a-f]+\b/gi, '<hex>')
    .replace(/\b(?=[0-9a-f]*\d)(?=\d*[a-f])[0-9a-f]{12,}\b/gi, '<hex>')
    .replace(/\d+(?:\.\d+)?/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_GROUPING_LENGTH)
}

function cap(text: string): string {
  return text.length > MAX_TEXT_LENGTH
    ? `${text.slice(0, MAX_TEXT_LENGTH - 1)}…`
    : text
}

/** a readable line for a value that is not an Error */
function describeValue(value: unknown): string {
  if (value === null) return 'Value of null'
  if (value === undefined) return 'Value of undefined'
  if (value === false) return 'Boolean value of false'
  if (value === 0) return 'Number value of 0'
  if (value === '') return 'Empty string'
  if (typeof value === 'object') {
    try {
      // undefined for a function-only object; throws for a cyclic one
      return cap(JSON.stringify(value) || Object.prototype.toString.call(value))
    } catch (e) {
      return 'Object that cannot be serialised'
    }
  }
  return cap(String(value))
}

/** codes written by the app server are UPPER_SNAKE (Apollo); Hasura's own are lower-kebab */
const isAppServerCode = (code: string | undefined): boolean =>
  code !== undefined && /^[A-Z][A-Z0-9_]*$/.test(code)

function messageOf(error: GraphQLErrorLike): string {
  return typeof error.message === 'string' ? error.message : ''
}

/** the response path of a GraphQL error: `['testStudy']` from the app server, `'$.selectionSet...'` from Hasura */
function pathOf(error: GraphQLErrorLike): string | undefined {
  if (Array.isArray(error.path) && error.path.length > 0) {
    return error.path.join('.')
  }
  const hasuraPath = error.extensions?.path
  return typeof hasuraPath === 'string' && hasuraPath ? hasuraPath : undefined
}

interface NetworkFailure {
  status: number | undefined
  statusText: string | undefined
  url: string | undefined
}

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

/** an Angular HttpErrorResponse (duck-typed: this file stays free of Angular) */
function httpFailureOf(value: unknown): NetworkFailure | undefined {
  if (
    !isObject(value) ||
    value.name !== 'HttpErrorResponse' ||
    typeof value.status !== 'number'
  ) {
    return undefined
  }
  return {
    status: value.status,
    statusText:
      typeof value.statusText === 'string' ? value.statusText : undefined,
    url: typeof value.url === 'string' ? value.url : undefined,
  }
}

/**
 * An XMLHttpRequest (duck-typed), bare or as `{ error: <the request> }`: the
 * DICOM image loader rejects a failed download with the request itself. As a
 * ThrownValue it was reported as a dump of the request's own properties
 * (Bugsnag's and zone.js's bookkeeping), without its status.
 */
function requestFailureOf(value: unknown): NetworkFailure | undefined {
  const request = isObject(value) && isObject(value.error) ? value.error : value
  if (
    !isObject(request) ||
    typeof request.readyState !== 'number' ||
    typeof request.status !== 'number'
  ) {
    return undefined
  }
  return {
    status: request.status,
    statusText:
      typeof request.statusText === 'string' && request.statusText
        ? request.statusText
        : undefined,
    // empty when there was no response
    url:
      typeof request.responseURL === 'string' && request.responseURL
        ? request.responseURL
        : undefined,
  }
}

/** the network or HTTP failure a value is or carries (an ApolloError's networkError) */
function networkFailureOf(value: unknown): NetworkFailure | undefined {
  const direct = httpFailureOf(value)
  if (direct) {
    return direct
  }
  if (isObject(value) && isObject(value.networkError)) {
    return (
      httpFailureOf(value.networkError) || {
        status: undefined,
        statusText:
          typeof value.networkError.message === 'string'
            ? value.networkError.message
            : undefined,
        url: undefined,
      }
    )
  }
  const request = requestFailureOf(value)
  if (request) {
    return request
  }
  // the wording of older Apollo clients, which helpers.ts still recognises
  if (
    value instanceof Error &&
    /Network error:.*0 Unknown Error/.test(value.message)
  ) {
    return { status: 0, statusText: undefined, url: undefined }
  }
  return undefined
}

/**
 * `/sendFiles` from `https://interior.myaqs.com/sendFiles?x=1`, ids replaced;
 * `/files/:file` for any stored file (each has its own name)
 */
function urlPathForGrouping(url: string): string {
  const withoutQuery = url.split(/[?#]/)[0]
  const path = withoutQuery.replace(/^[a-z]+:\/\/[^/]+/i, '') || '/'
  if (/^\/files\/./.test(path)) {
    return '/files/:file'
  }
  return path.replace(/\/\d+(?=\/|$)/g, '/:id')
}

function describeNetworkFailure(failure: NetworkFailure): string {
  if (failure.status === 0) {
    return 'no response (HTTP status 0)'
  }
  if (failure.status === undefined) {
    return failure.statusText || 'network error'
  }
  return `HTTP ${failure.status}${
    failure.statusText ? ` ${failure.statusText}` : ''
  }`
}

/** own data a named MessageError subclass carries (ReevaluationError's failures), for its tab */
function ownData(error: Error): { [key: string]: unknown } | undefined {
  const data: { [key: string]: unknown } = {}
  Object.keys(error).forEach((key) => {
    if (key !== 'name' && key !== 'message' && key !== 'stack') {
      data[key] = ((error as unknown) as { [key: string]: unknown })[key]
    }
  })
  return Object.keys(data).length > 0 ? data : undefined
}

function classifyGraphQLErrors(
  errors: GraphQLErrorLike[],
  operationName: string | undefined,
): Classification {
  // what the tech was told wins over whatever else the response lists
  const primary =
    errors.find((error) => getUserFacingDetails(error) !== undefined) ||
    errors.find((error) => getAnalysisFailure(error) !== undefined) ||
    errors[0]
  const userFacing = getUserFacingDetails(primary)
  const analysis = getAnalysisFailure(primary)
  const code = codeOf(primary)
  const text = messageOf(primary)
  const path = pathOf(primary)
  const operation =
    operationName ||
    (Array.isArray(primary.path) && typeof primary.path[0] === 'string'
      ? primary.path[0]
      : undefined) ||
    'unknown operation'
  const response = { code, path }

  if (code === 'invalid-jwt') {
    return {
      errorClass: 'SessionExpired',
      severity: 'info',
      message: `${operation}: ${text}`,
      groupingHash: 'session-expired',
      response,
    }
  }
  if (userFacing !== undefined) {
    const reason = userFacing.reason || 'UNKNOWN'
    return {
      errorClass: 'UserFacingError',
      severity: 'info',
      message: userFacing.message,
      groupingHash: `user-facing:${reason}`,
      response: { ...response, reason },
      tabs: { 'user facing': { reason, message: userFacing.message } },
    }
  }
  if (analysis !== undefined) {
    const reason = analysis.reason || 'UNKNOWN'
    const phantom = analysis.phantom || 'unknown phantom'
    const detail = analysis.detail || analysis.message
    return {
      errorClass: 'AnalysisError',
      severity: 'error',
      message: `${operation}: ${detail}`,
      groupingHash: `analysis:${phantom}:${reason}:${normaliseForGrouping(
        detail,
      )}`,
      response: { ...response, reason },
      analysis,
    }
  }
  if (code === 'INTERNAL_SERVER_ERROR') {
    return {
      errorClass: 'ServerError',
      severity: 'error',
      message: `${operation}: ${text}`,
      groupingHash: `server:${operation}:${normaliseForGrouping(text)}`,
      response,
    }
  }
  if (isAppServerCode(code)) {
    const reason =
      typeof primary.extensions?.reason === 'string'
        ? primary.extensions.reason
        : undefined
    return {
      errorClass: 'ServerMessage',
      severity: 'info',
      message: `${operation}: ${code}: ${text}`,
      groupingHash: `server-message:${code}:${operation}`,
      response: { ...response, reason },
    }
  }
  return {
    errorClass: 'HasuraError',
    severity: 'error',
    message: `${operation}: ${text}`,
    // with the operation: the same refusal (a permission check, say) from a
    // second mutation is a second thing to fix
    groupingHash: `hasura:${code || 'no-code'}:${operation}:${normaliseForGrouping(
      text,
    )}`,
    response,
  }
}

/**
 * Classify whatever reached the global error handler or was reported by a
 * quiet handler. Looks inside ApolloErrors, bare GraphQL error objects, lists
 * of them and zone-wrapped rejections alike. Never throws.
 */
export function classify(
  thrown: unknown,
  context: ErrorContext = {},
): Classification {
  const value = unwrap(thrown)
  const operationName = context.operation?.name

  if (value instanceof HandledFailure) {
    const cause = classify(value.cause, context)
    return {
      errorClass: 'HandledFailure',
      severity: 'warning',
      message: `${value.operation}: ${cause.errorClass}: ${cause.message}`,
      // with the class of the cause: a dropped connection and a refused
      // query behind the same handler are different problems
      groupingHash: `handled:${value.operation}:${cause.errorClass}`,
      response: cause.response,
      analysis: cause.analysis,
      tabs: {
        ...cause.tabs,
        handled: { operation: value.operation, cause: cause.errorClass },
      },
    }
  }

  if (value instanceof NotFoundError) {
    return {
      errorClass: 'NotFoundError',
      severity: 'info',
      message: value.message,
      groupingHash: `not-found:${value.entity}`,
      tabs: { 'not found': { entity: value.entity, id: value.id } },
    }
  }

  // The router's "Cannot match any routes": an address no route matches,
  // typed or pasted wrong, or an old bookmark. The global handler opens the
  // home page instead (sign-in when logged out), so the user carries on. One
  // group for every address, a link in the app with a wrong path included;
  // the message names the part the router could not match.
  if (isNavError(value)) {
    return {
      errorClass: 'NotFoundError',
      severity: 'info',
      message: (value as Error).message,
      groupingHash: 'not-found:page',
      tabs: { 'not found': { entity: 'page' } },
    }
  }

  const graphQLErrors = graphQLErrorsOf(value)
  if (graphQLErrors.length > 0) {
    return classifyGraphQLErrors(graphQLErrors, operationName)
  }

  const network = networkFailureOf(value)
  if (network) {
    const what =
      operationName ||
      (network.url ? urlPathForGrouping(network.url) : 'unknown request')
    // no response at all says nothing about the operation that happened to
    // be on its way: one group, or a dropped connection is one per operation
    const noResponse = network.status === undefined || network.status === 0
    return {
      errorClass: 'NetworkError',
      severity: 'warning',
      message: `${what}: ${describeNetworkFailure(network)}`,
      groupingHash: noResponse
        ? 'network:no-response'
        : `network:${network.status}:${what}`,
      response: { status: network.status },
    }
  }

  if (value instanceof MessageError) {
    // a subclass that names itself (ReevaluationError) is its own class and
    // one group; a plain MessageError is a sentence the user was shown
    if (value.name && value.name !== 'Error') {
      const data = ownData(value)
      return {
        errorClass: value.name,
        severity: 'warning',
        message: value.message,
        groupingHash: `user-message:${value.name}`,
        tabs: data ? { [value.name]: data } : undefined,
      }
    }
    return {
      errorClass: 'UserMessage',
      severity: 'info',
      message: value.message,
      groupingHash: `user-message:${normaliseForGrouping(value.message)}`,
    }
  }

  if (value instanceof Error) {
    return {
      errorClass: value.name || 'Error',
      severity: 'error',
      message: value.message,
    }
  }

  const text = describeValue(value)
  return {
    errorClass: 'ThrownValue',
    severity: 'warning',
    message: text,
    groupingHash: `thrown:${normaliseForGrouping(text)}`,
  }
}
